// utils/SoundManager.js
import { HowlCache } from "./State/Slices/soundsSlice"; // ← use the Redux cache
import { Howler } from "howler";
import { SOUND_NAME, SOUND_VOLUMES } from "./sounds";
import StorageManager from "./utils/StorageManager";
import { FEATURES_CONFIG } from "./featuresConfig";
import TickerTimeout from "./utils/TickerTimeout";

// ── NOTHING SOUNDS WHILE THE INTRO SCREEN IS UP ──
//
// Vladimir, 2026-09-22: "no sounds until we close intro screen, besides intro
// screen sound itself."
//
// The game builds its whole scene behind that popup — the reels, the free-spins
// room, the reload popup, every pooled spine being armed — and anything that
// fires a sound while doing it is heard over a screen that has not started yet.
// Two were reported: the reload popup announcing itself on load, and the free
// spins arming their animations during a scene rebuild.
//
// Gated centrally rather than at each call site, because the call sites are the
// problem: there are hundreds, they belong to code that has no idea a popup is
// up, and every new one would have to remember.
//
// These three are the intro's OWN voice and must pass.
const INTRO_ALLOWED_SOUNDS = new Set([
  SOUND_NAME.intro_music,
  SOUND_NAME.button_click,
  SOUND_NAME.tap_to_continue,
]);

class SoundManager {
  constructor() {
    // Load saved settings from localStorage
    const savedSettings = StorageManager.loadSoundSettings();

    // Initialize with saved values or defaults
    this.soundEffectsEnabled = savedSettings.soundEffectsEnabled;
    this.backgroundMusicEnabled = savedSettings.backgroundMusicEnabled;
    this.globalSoundDisabled = savedSettings.globalSoundDisabled;
    this.masterVolume = savedSettings.masterVolume;
    Howler.volume(this.masterVolume);
    // Restore last active music track — falls back to base game music
    this.currentBackgroundMusic = savedSettings.currentBackgroundMusic || SOUND_NAME.background_music;
    this._activeSpriteIds = {};
    // Pending stop() timers armed by fadeOut(), keyed by sound name, so a sound
    // that is started again before its fade lands can cancel them.
    this._fadeTimers = {};
    // Ticker timers that stop a sprite one-shot once its fade has run.
    this._oneShotFades = {};
    // Names already played in the CURRENT animation frame — see
    // playOncePerFrame. A real rAF, not the ticker: this is frame identity,
    // not a delay, and it must clear even when the PIXI ticker is stopped.
    this._frameSounds = new Set();
    this._frameSoundRAF = null;
    // When true, ambient music is playing instead of background music
    this.isAmbientActive = false;
    // When true, music toggle saves the setting but doesn't start playback.
    // releaseMusicPlayback() starts playback when intro popup closes.
    this.musicPlaybackDeferred = true;
  }

  /**
   * Is this sound one the intro screen is not allowed to be talking over?
   *
   * `musicPlaybackDeferred` ALREADY means "the intro popup has not closed yet":
   * it starts true and releaseMusicPlayback() is the one thing that clears it.
   * So the gate reuses it rather than adding a second flag that could disagree
   * with the first.
   */
  _mutedByIntroScreen(name) {
    return this.musicPlaybackDeferred && !INTRO_ALLOWED_SOUNDS.has(name);
  }

  /**
   * Call when intro popup closes — starts background music if enabled.
   * Determines the correct track from game state (not localStorage) to avoid stale values.
   */
  releaseMusicPlayback() {
    this.musicPlaybackDeferred = false;

    // Determine the correct music from current game state.
    // localStorage can be stale (e.g., bonus ended but localStorage wasn't cleared yet).
    try {
      const { default: store } = require('./State/Store/store');
      const state = store.getState();
      // Every feature that owns the screen has to be named here, or a RELOAD
      // straight into that feature starts the base game track. There is no
      // transition on a reload — the player is restored into the room — so
      // this is the ONLY place the right track gets chosen.
      const inFeature =
        state.potCollection?.potCollectionActive === true ||
        state.freeSpins?.active === true;
      // const correctMusic = inFeature
      //   ? SOUND_NAME.feature_background_music
      //   : SOUND_NAME.background_music;
      // Hold & Win first: it has its own track (2026-10-19) and can sit
      // inside free spins, where both flags are up.
      const correctMusic = state.holdAndWin?.active === true
        ? SOUND_NAME.hold_and_win_music
        : inFeature
          ? SOUND_NAME.feature_background_music
          : SOUND_NAME.background_music;
      this._setCurrentBackgroundMusic(correctMusic);
    } catch (e) {
      // Store not ready — fall through to localStorage value
    }

    if (this.backgroundMusicEnabled && !this.globalSoundDisabled) {
      this.play(this.currentBackgroundMusic);
    }
  }

  /**
   * TEMPORARY. Print the numbers that decide how loud a sound really is.
   * Gated on FEATURES_CONFIG.DEV_TOOLS.SOUND_DIAGNOSTICS.
   * @private
   */
  _diag(where, name, extra = {}) {
    if (!FEATURES_CONFIG.DEV_TOOLS?.SOUND_DIAGNOSTICS) return;
    const sprite = HowlCache["sfx_sprite"];
    const isSprite = !!(sprite && sprite._sprite && sprite._sprite[name]);
    const howl = isSprite ? sprite : HowlCache[name];
    try {
      // eslint-disable-next-line no-console
      console.log(
        `[snd] ${where} ${name}`,
        JSON.stringify({
          sprite: isSprite,
          configured: this._getSoundVolume(name),
          howlVolume: howl ? howl.volume() : null,
          muted: howl ? howl.mute() : null,
          playing: howl ? howl.playing() : null,
          master: Howler.volume(),
          fx: this.soundEffectsEnabled,
          music: this.backgroundMusicEnabled,
          ...extra,
        }),
      );
    } catch (e) {
      // Silent fail
    }
  }

  /**
   * Cancel the stop() timers a previous fadeOut() armed for this sound.
   *
   * fadeOut() schedules `sound.stop()` for `duration` ms later. If the same
   * sound is started again inside that window — the next win begins before the
   * last one's fade has landed — that stale timer fires INTO the new playback
   * and kills it. The sound then reads as missing or barely there, at random,
   * depending on how the two windows line up.
   * @private
   */
  _cancelPendingFade(name) {
    const timers = this._fadeTimers[name];
    if (!timers) return;
    if (timers.stop) clearTimeout(timers.stop);
    if (timers.ambient) clearTimeout(timers.ambient);
    delete this._fadeTimers[name];
  }

  /**
   * Get the configured volume for a sound (from SOUND_VOLUMES map).
   * Falls back to 1.0 if not listed.
   * @private
   */
  _getSoundVolume(name) {
    return SOUND_VOLUMES[name] ?? 1.0;
  }

  /**
   * Play a sound from the shared HowlCache
   * Handles both individual sounds and audio sprite sounds
   */
  play(name) {
    if (this._mutedByIntroScreen(name)) return;
    if (this.globalSoundDisabled) {
      // Still track which music SHOULD be playing so re-enabling picks the right track
      if (this._isBackgroundMusic(name)) {
        this._setCurrentBackgroundMusic(name);
      }
      return;
    }

    // Check if this sound is in the sprite
    const spriteSound = HowlCache['sfx_sprite'];
    if (spriteSound && spriteSound._sprite && spriteSound._sprite[name]) {
      // It's a sprite sound - play the specific sprite
      if (this.soundEffectsEnabled) {
        const id = spriteSound.play(name);
        this._diag("play/sprite", name, { id });
        // Apply per-sound volume to this sprite playback instance
        const vol = this._getSoundVolume(name);
        if (vol < 1.0) {
          spriteSound.volume(vol, id);
        }
        this._activeSpriteIds[name] = id;
      }
      return;
    }

    // Not a sprite sound - play as individual sound (looping sounds)
    const sound = HowlCache[name];
    if (!sound) {
      return;
    }

    // An explicit start wins over a fade that has not landed yet.
    this._cancelPendingFade(name);

    // Apply per-sound volume
    const vol = this._getSoundVolume(name);
    sound.volume(vol);
    this._diag("play/howl", name);

    if (this._isMusic(name)) {
      // Track which background music should be active (even if muted),
      // so enabling music later plays the correct track for the current scene.
      // Guard: skip during startup (musicPlaybackDeferred) to avoid the base game
      // scene init overwriting the saved bonus track before releaseMusicPlayback fires.
      if (this._isBackgroundMusic(name) && !this.musicPlaybackDeferred) {
        this._setCurrentBackgroundMusic(name);
      }

      if (this.backgroundMusicEnabled) {
        sound.mute(false);
        if (!sound.playing()) {
          sound.play();
        }

        // GDD: start paired ambient track silently (vol 0) so both play in sync.
        // Crossfade only adjusts volumes — never starts/stops tracks.
        if (this._isBackgroundMusic(name)) {
          this._startAmbientSilently(name);
        }
      }
    } else if (this.soundEffectsEnabled) {
      sound.mute(false);
      sound.play();
    }
  }

  /**
   * Play a LOOPING sound exactly ONCE.
   *
   * Used as an end stab: the counter ticks on a loop while the numbers run,
   * and the same sound marks the moment they land. play() would start it
   * looping again, so the new sound id has its loop cleared.
   */
  playOnce(name) {
    if (this._mutedByIntroScreen(name)) return;
    if (this.globalSoundDisabled || !this.soundEffectsEnabled) return;
    const sound = HowlCache[name];
    if (!sound) return;
    this._cancelPendingFade(name);
    sound.volume(this._getSoundVolume(name));
    sound.mute(false);
    const id = sound.play();
    sound.loop(false, id);
  }

  /**
   * Play a one-shot AT MOST ONCE per animation frame.
   *
   * For an event that is one thing to the player but many calls in the code:
   * every mystery on the board starts its build-up in the same frame, and a
   * volley of trails fires every bolt at once. Played straight, that is ten
   * copies of one file starting together — ten times the level and a comb
   * filter, not an effect.
   *
   * The gate is shared by every caller, so two different controllers running
   * the same event in one frame still make one sound. Cleared on the next
   * frame by a single rAF, so the next frame's event is heard normally.
   *
   * Same proven pattern as the private copies in fireLightningBolt.js,
   * useLandingAnimations.js and LevelChargingAnimator.js — those predate this
   * method and are left alone.
   */
  playOncePerFrame(name) {
    if (!name || this._frameSounds.has(name)) return;
    this._frameSounds.add(name);
    this.play(name);
    if (this._frameSoundRAF === null) {
      this._frameSoundRAF = requestAnimationFrame(() => {
        this._frameSounds.clear();
        this._frameSoundRAF = null;
      });
    }
  }

  /**
   * Fade a SPRITE one-shot out over `duration`, then stop it.
   *
   * `fadeOut()` cannot do this: it looks the sound up in HowlCache, and sprite
   * one-shots live inside the shared `sfx_sprite` Howl, so it returns early and
   * does nothing. Here the tracked sound id is faded directly.
   *
   * Used when a banner is CUT SHORT by a skip — an immediate stop chops it
   * mid-note. Falls back to a plain stop when the sound is not a sprite entry
   * or is not currently playing, so callers can use it unconditionally.
   *
   * The stop is a TickerTimeout, not setTimeout: house rule, and it keeps the
   * stop on the same clock as the animation it belongs to.
   */
  fadeOutOneShot(name, duration = 50) {
    const spriteSound = HowlCache["sfx_sprite"];
    const id = this._activeSpriteIds[name];
    const isSprite = !!(spriteSound && spriteSound._sprite && spriteSound._sprite[name]);

    if (!isSprite || id === undefined || !spriteSound.playing(id)) {
      this.stop(name);
      return;
    }

    // One fade per sound at a time — a second skip must not leave the first
    // fade's stop armed against a sound id that has already been replaced.
    this._oneShotFades[name]?.clear();
    try {
      spriteSound.fade(spriteSound.volume(id), 0, duration, id);
    } catch (e) {
      this.stop(name);
      return;
    }

    this._oneShotFades[name] = new TickerTimeout(() => {
      delete this._oneShotFades[name];
      try {
        spriteSound.stop(id);
      } catch (e) {
        // Already gone
      }
      if (this._activeSpriteIds[name] === id) delete this._activeSpriteIds[name];
    }, duration);
  }

  stop(name) {
    // Check sprite sounds first
    const spriteSound = HowlCache['sfx_sprite'];
    if (spriteSound && spriteSound._sprite && spriteSound._sprite[name]) {
      const id = this._activeSpriteIds[name];
      if (id !== undefined) {
        spriteSound.stop(id);
        delete this._activeSpriteIds[name];
      }
      return;
    }

    const sound = HowlCache[name];
    if (sound && sound.playing()) {
      sound.stop();

      // If stopping background/feature music, also stop its paired ambient
      if (this._isBackgroundMusic(name)) {
        this.isAmbientActive = false;
        const ambientName = this._getAmbientFor(name);
        if (ambientName) {
          const ambientMusic = HowlCache[ambientName];
          if (ambientMusic && ambientMusic.playing()) {
            ambientMusic.stop();
          }
        }
      }
    }
  }

  /**
   * Fade out a sound gradually over the specified duration
   * @param {string} name - Sound name to fade out
   * @param {number} duration - Duration in milliseconds (default: 1000ms)
   * @param {Function} onComplete - Optional callback when fade completes
   */
  fadeOut(name, duration = 1000, onComplete = null) {
    const sound = HowlCache[name];
    if (!sound) {
      if (onComplete) onComplete();
      return;
    }

    if (!sound.playing()) {
      if (onComplete) onComplete();
      return;
    }

    this._cancelPendingFade(name);
    const timers = {};
    this._fadeTimers[name] = timers;

    const currentVolume = sound.volume();
    sound.fade(currentVolume, 0, duration);
    this._diag("fadeOut", name, { from: currentVolume, duration });

    // If fading out background/feature music, also stop any playing ambient
    if (this._isBackgroundMusic(name)) {
      this.isAmbientActive = false;
      const ambientName = this._getAmbientFor(name);
      if (ambientName) {
        const ambientMusic = HowlCache[ambientName];
        if (ambientMusic && ambientMusic.playing()) {
          const ambientVolume = ambientMusic.volume();
          ambientMusic.fade(ambientVolume, 0, duration);
          timers.ambient = setTimeout(() => {
            ambientMusic.stop();
            ambientMusic.volume(this._getSoundVolume(ambientName));
          }, duration);
        }
      }
    }

    // Stop the sound after fade completes
    timers.stop = setTimeout(() => {
      delete this._fadeTimers[name];
      sound.stop();
      // Restore the sound's OWN configured volume for the next play — never
      // masterVolume. Howler.volume() already applies master globally, so
      // writing it onto one Howl applies it twice (0.7 x 0.7 = 0.49) and that
      // track comes back quiet for ever after. The ambient branch above always
      // did this correctly; this line did not.
      sound.volume(this._getSoundVolume(name));
      if (onComplete) onComplete();
    }, duration);
  }

  /**
   * Fade in a sound gradually over the specified duration
   * @param {string} name - Sound name to fade in
   * @param {number} duration - Duration in milliseconds (default: 1000ms)
   * @param {number} targetVolume - Target volume (default: uses masterVolume)
   * @param {Function} onComplete - Optional callback when fade completes
   */
  fadeIn(name, duration = 1000, targetVolume = null, onComplete = null) {
    if (this._mutedByIntroScreen(name)) {
      // The caller is owed its callback either way — a fade that never runs
      // must not leave a sequence waiting on it.
      if (onComplete) onComplete();
      return;
    }
    const sound = HowlCache[name];
    if (!sound) {
      if (onComplete) onComplete();
      return;
    }

    if (this.globalSoundDisabled) {
      // Still track which music SHOULD be playing so re-enabling picks the right track
      if (this._isBackgroundMusic(name)) {
        this._setCurrentBackgroundMusic(name);
      }
      if (onComplete) onComplete();
      return;
    }

    // Check if it's music and if music is enabled
    if (this._isMusic(name)) {
      if (!this.backgroundMusicEnabled) {
        // Still track which music SHOULD be playing
        if (this._isBackgroundMusic(name)) {
          this._setCurrentBackgroundMusic(name);
        }
        if (onComplete) onComplete();
        return;
      }
    } else {
      if (!this.soundEffectsEnabled) {
        if (onComplete) onComplete();
        return;
      }
    }

    // Use per-sound volume as default target (respects SOUND_VOLUMES config)
    const actualTargetVolume = targetVolume !== null ? targetVolume : this._getSoundVolume(name);

    // Auto-track which background music is active (skip ambient tracks)
    if (this._isBackgroundMusic(name)) {
      this._setCurrentBackgroundMusic(name);
    }

    // An explicit start wins over a fade that has not landed yet — otherwise
    // the old fadeOut's stop() fires mid-fade and silences this playback.
    this._cancelPendingFade(name);

    // Set initial volume to 0 and start playing
    sound.volume(0);
    sound.mute(false);
    sound.play();

    // Fade in to target volume
    sound.fade(0, actualTargetVolume, duration);
    this._diag("fadeIn", name, { target: actualTargetVolume, duration });

    // Reset ambient state — background music is now active
    // Also restart ambient silently so both tracks play in sync
    if (this._isBackgroundMusic(name)) {
      this.isAmbientActive = false;
      this._startAmbientSilently(name);
    }

    // Call onComplete after fade completes
    if (onComplete) {
      setTimeout(() => {
        onComplete();
      }, duration);
    }
  }

  toggleGlobal(disabled) {
    this.globalSoundDisabled = disabled;
    Object.values(HowlCache).forEach((sound) => {
      sound.mute(disabled);
      if (disabled) sound.stop();
    });
    this.soundEffectsEnabled = !disabled;
    this.backgroundMusicEnabled = !disabled;

    // If re-enabling, start current background music + ambient at vol 0
    if (!disabled && this.backgroundMusicEnabled && !this.musicPlaybackDeferred) {
      this.isAmbientActive = false;
      const currentMusic = HowlCache[this.currentBackgroundMusic];
      if (currentMusic) {
        currentMusic.mute(false);
        if (!currentMusic.playing()) currentMusic.play();
      }
      this._startAmbientSilently(this.currentBackgroundMusic);
    }
  }

  toggleBackgroundMusic(enabled) {
    this.backgroundMusicEnabled = enabled;
    const current = HowlCache[this.currentBackgroundMusic];
    const ambientName = this._getAmbientFor(this.currentBackgroundMusic);
    const ambientMusic = ambientName ? HowlCache[ambientName] : null;

    if (enabled && !this.globalSoundDisabled && !this.musicPlaybackDeferred) {
      // Start background music + ambient at vol 0 (both play in sync)
      this.isAmbientActive = false;
      if (current) {
        current.mute(false);
        current.play();
      }
      this._startAmbientSilently(this.currentBackgroundMusic);
    } else {
      this.isAmbientActive = false;
      if (current) {
        current.stop();
      }
      // Also stop ambient
      if (ambientMusic && ambientMusic.playing()) {
        ambientMusic.stop();
      }
      // ── And any music that is NOT the background track ──
      //
      // This used to stop `currentBackgroundMusic` alone, so switching music
      // off during a win celebration or with the intro popup up left that loop
      // running — the one thing the player was trying to stop. Gating the START
      // is not enough on its own; the toggle has to reach what is already
      // playing. See _isEventMusic.
      // for (const name of [SOUND_NAME.win_music, SOUND_NAME.intro_music]) {
      // for (const name of [SOUND_NAME.win_music, SOUND_NAME.total_win_music, SOUND_NAME.intro_music]) {
      for (const name of [SOUND_NAME.win_music, SOUND_NAME.total_win_music, SOUND_NAME.royal_music, SOUND_NAME.intro_music]) {
        const sound = HowlCache[name];
        if (sound && sound.playing()) sound.stop();
      }
    }
  }

  /**
   * Switch to a different background music track
   * @param {string} musicName - The music track to switch to
   */
  switchBackgroundMusic(musicName) {
    const wasPlaying = this.backgroundMusicEnabled && !this.globalSoundDisabled;

    // Stop current music (background or ambient) if playing
    if (this.currentBackgroundMusic) {
      const currentMusic = HowlCache[this.currentBackgroundMusic];
      if (currentMusic && currentMusic.playing()) {
        currentMusic.stop();
      }
      // Always stop paired ambient too (it's always playing at vol 0 or higher)
      const ambientName = this._getAmbientFor(this.currentBackgroundMusic);
      if (ambientName) {
        const ambientMusic = HowlCache[ambientName];
        if (ambientMusic && ambientMusic.playing()) {
          ambientMusic.stop();
        }
      }
    }

    // Reset ambient state — new scene always starts with background music
    this.isAmbientActive = false;

    // Update current music reference and persist
    this._setCurrentBackgroundMusic(musicName);

    // Play new music + start paired ambient silently
    if (wasPlaying) {
      const newMusic = HowlCache[musicName];
      if (newMusic) {
        newMusic.mute(false);
        newMusic.play();
      }
      this._startAmbientSilently(musicName);
    }
  }

  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.masterVolume);
  }

  // ── Ambient / Background crossfade (inactivity-driven) ──────────────────
  // GDD approach: both tracks play simultaneously from the start.
  // Crossfade only adjusts volumes — tracks are never started/stopped here.

  /**
   * Crossfade from background music to its paired ambient music.
   * Called after a period of player inactivity.
   * Both tracks are already playing (ambient at vol 0). Just swap volumes.
   * @param {number} duration - Crossfade duration in ms (default: 2000)
   */
  crossfadeToAmbient(duration = 2000) {
    if (this.isAmbientActive) return;
    if (this.globalSoundDisabled || !this.backgroundMusicEnabled) return;
    if (this.musicPlaybackDeferred) return;

    const bgName = this.currentBackgroundMusic;
    const ambientName = this._getAmbientFor(bgName);
    if (!ambientName) return;

    const bgSound = HowlCache[bgName];
    const ambientSound = HowlCache[ambientName];
    if (!bgSound || !ambientSound) return;

    this.isAmbientActive = true;

    // Fade background volume down to 0
    if (bgSound.playing()) {
      bgSound.fade(bgSound.volume(), 0, duration);
    }

    // Fade ambient volume up to its target
    const targetVol = this._getSoundVolume(ambientName);
    if (ambientSound.playing()) {
      ambientSound.fade(ambientSound.volume(), targetVol, duration);
    }
  }

  /**
   * Crossfade from ambient music back to its paired background music.
   * Called when the player becomes active (e.g. starts a spin).
   * Both tracks are already playing. Just swap volumes.
   * @param {number} duration - Crossfade duration in ms (default: 2000)
   */
  crossfadeToBackground(duration = 2000) {
    if (!this.isAmbientActive) return;
    if (this.globalSoundDisabled || !this.backgroundMusicEnabled) return;

    const bgName = this.currentBackgroundMusic;
    const ambientName = this._getAmbientFor(bgName);
    if (!ambientName) return;

    const bgSound = HowlCache[bgName];
    const ambientSound = HowlCache[ambientName];
    if (!bgSound || !ambientSound) return;

    this.isAmbientActive = false;

    // Fade ambient volume down to 0
    if (ambientSound.playing()) {
      ambientSound.fade(ambientSound.volume(), 0, duration);
    }

    // Fade background volume up to its target
    const targetVol = this._getSoundVolume(bgName);
    if (bgSound.playing()) {
      bgSound.fade(bgSound.volume(), targetVol, duration);
    }
  }

  /**
   * Returns the ambient music track name that pairs with a given background
   * music.
   *
   *   background_music          -> ambient_music
   *   feature_background_music  -> feature_ambient_music
   *
   * ── Both currently resolve to the SAME track ──
   *
   * Mad Labs ships one ambient bed for both rooms, so `SOUND_NAME` points
   * `feature_ambient_music` at "ambient_music". The pairing below is left
   * spelled out rather than collapsed: it is what a second file would need, and
   * dropping in one means changing SOUND_NAME alone.
   *
   * Sharing one Howl across the rooms is safe because a background change goes
   * through `fadeIn`, which clears `isAmbientActive` and calls
   * `_startAmbientSilently` — and that sets the ambient's volume to 0
   * unconditionally. So the bed keeps playing in sync through the wipe and is
   * silent again on the other side.
   *
   * @returns {string|null}
   */
  _getAmbientFor(name) {
    if (name === SOUND_NAME.background_music && SOUND_NAME.ambient_music) {
      return SOUND_NAME.ambient_music;
    }
    if (name === SOUND_NAME.feature_background_music && SOUND_NAME.feature_ambient_music) {
      return SOUND_NAME.feature_ambient_music;
    }
    // Hold & Win's own track shares the feature rooms' ambient (2026-10-19).
    if (name === SOUND_NAME.hold_and_win_music && SOUND_NAME.feature_ambient_music) {
      return SOUND_NAME.feature_ambient_music;
    }
    return null;
  }

  /**
   * Update currentBackgroundMusic and persist to localStorage.
   * Called automatically by play() and fadeIn() for music tracks.
   */
  _setCurrentBackgroundMusic(name) {
    this.currentBackgroundMusic = name;
    StorageManager.saveSoundSettings({
      masterVolume: this.masterVolume,
      soundEffectsEnabled: this.soundEffectsEnabled,
      backgroundMusicEnabled: this.backgroundMusicEnabled,
      globalSoundDisabled: this.globalSoundDisabled,
      currentBackgroundMusic: name,
    });
  }

  /**
   * Check if a sound name is a background music track (not ambient).
   * These are the tracks that currentBackgroundMusic can be set to.
   */
  _isBackgroundMusic(name) {
    return [
      SOUND_NAME.background_music,
      SOUND_NAME.feature_background_music,
      SOUND_NAME.free_spin_background_music,
      SOUND_NAME.hold_and_win_music,
    ].includes(name);
  }

  /**
   * Music that is NOT a background track.
   *
   * It belongs to a MOMENT rather than to a scene: the win celebration's loop,
   * which replaces the background track for the length of the sequence, and the
   * intro popup's loop, which plays before any scene exists. Both are music by
   * every meaning the player has, but neither may go into
   * `currentBackgroundMusic` — that is the track a reload restores — and
   * neither has a paired ambient.
   *
   * Which is exactly how they came to be wrong. `_isMusic` listed only the
   * background and ambient tracks, so these two fell through to the effects
   * branch: the MUSIC toggle did not silence them and the SOUND EFFECTS toggle
   * did. Turning the music off left the win celebration singing.
   */
  _isEventMusic(name) {
    // return [SOUND_NAME.win_music, SOUND_NAME.intro_music].includes(name);
    // return [SOUND_NAME.win_music, SOUND_NAME.total_win_music, SOUND_NAME.intro_music].includes(name);
    return [SOUND_NAME.win_music, SOUND_NAME.total_win_music, SOUND_NAME.royal_music, SOUND_NAME.intro_music].includes(name);
  }

  _isMusic(name) {
    if (this._isBackgroundMusic(name)) return true;
    // Not a scene's track, but music all the same — see _isEventMusic.
    if (this._isEventMusic(name)) return true;
    // Ambient tracks are also music
    if (SOUND_NAME.ambient_music && name === SOUND_NAME.ambient_music) return true;
    if (SOUND_NAME.feature_ambient_music && name === SOUND_NAME.feature_ambient_music) return true;
    return false;
  }

  /**
   * Start the paired ambient track silently (volume 0) alongside its background music.
   * Both tracks play in sync from the start — crossfade only adjusts volumes.
   * @param {string} bgName - The background music track name
   * @private
   */
  _startAmbientSilently(bgName) {
    const ambientName = this._getAmbientFor(bgName);
    if (!ambientName) return;

    const ambientSound = HowlCache[ambientName];
    if (!ambientSound) return;

    // Start at volume 0 so it plays in sync but is inaudible
    ambientSound.volume(0);
    ambientSound.mute(false);
    if (!ambientSound.playing()) {
      ambientSound.play();
    }
  }

}

export const soundManager = new SoundManager();
